import React from "react";
import { Link } from "react-router-dom";
import { useMitmcvc } from "./MitmcvcLayout";
import { TRADES, FEATURES } from "./_shared";
import { Car, ShieldCheck, ArrowRight, CheckCircle2, GraduationCap, Building2 } from "lucide-react";

export default function Home() {
  const data = useMitmcvc();
  const inst = data?.institute || {};
  const trade = TRADES[0];

  return (
    <div data-testid="mitmcvc-home">
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#14532D] via-[#166534] to-[#052E16] text-white">
        <div className="absolute inset-0 opacity-[0.12]" style={{ backgroundImage: "radial-gradient(circle at 25% 25%, #F59E0B 0, transparent 40%), radial-gradient(circle at 75% 75%, #FEF8E8 0, transparent 40%)" }} />
        <div className="relative max-w-7xl mx-auto px-5 py-16 md:py-24 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase font-bold text-[#F59E0B] bg-[#F59E0B]/10 border border-[#F59E0B]/30 px-3 py-1.5 rounded-full">
              HSC बोर्ड संलग्न · स्थापना १९८८–८९
            </div>
            <h1 className="mt-5 font-serif font-black text-3xl md:text-5xl leading-[1.15]">
              कौशल्याचे शिक्षण, <br /><span className="text-[#F59E0B]">रोजगाराचे भवितव्य.</span>
            </h1>
            <p className="mt-5 text-white/90 text-base md:text-lg max-w-xl leading-relaxed">
              एम.आय.टी. एच.एस.सी. व्होकेशनल (एम.सी.व्ही.सी.) कनिष्ठ महाविद्यालय — छत्रपती संभाजीनगर जिल्ह्यातील दर्जेदार तांत्रिक शिक्षण देणारी सुसज्ज संस्था. <span className="text-[#F59E0B] font-semibold">३०००+</span> प्रशिक्षणार्थी आजपर्यंत कंपन्यांत व शासकीय आस्थापनांत रुजू.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="about" className="inline-flex items-center gap-2 bg-[#F59E0B] hover:bg-[#B45309] text-[#052E16] font-bold text-sm uppercase tracking-wider px-6 py-3.5 shadow-lg shadow-[#F59E0B]/20 transition-all hover:-translate-y-0.5" data-testid="mitmcvc-hero-explore">
                आमच्याविषयी <ArrowRight size={16} />
              </Link>
              <Link to="contact" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur border border-white/25 text-white font-bold text-sm uppercase tracking-wider px-6 py-3.5 transition-all" data-testid="mitmcvc-hero-contact">
                भेटी करा / संपर्क
              </Link>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-5 max-w-lg">
              <Stat kpi="३०००+" label="माजी विद्यार्थी" />
              <Stat kpi="३५+" label="वर्षे शिक्षण" />
              <Stat kpi="६०" label="वार्षिक जागा" />
            </div>
          </div>
          <div className="relative">
            <div className="aspect-[4/3] rounded-3xl overflow-hidden bg-white/5 border border-white/10 shadow-2xl">
              <img src={inst.logo_url || "/assets/mitcsn-logo.png"} alt={inst.name} className="w-full h-full object-contain p-16 md:p-20 bg-gradient-to-br from-white to-[#FEF8E8]" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-white text-[#14532D] p-4 shadow-xl border-l-4 border-[#F59E0B] hidden md:block">
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#B45309] font-bold">HSC संलग्नता क्र.</div>
              <div className="font-mono font-black text-lg mt-0.5">56.01.066</div>
              <div className="text-[10px] tracking-[0.15em] uppercase text-slate-500 mt-0.5">U-DISE · 90100000095</div>
            </div>
          </div>
        </div>
      </section>

      {/* Program strip */}
      <section className="max-w-6xl mx-auto px-5 py-16">
        <div className="text-center max-w-3xl mx-auto">
          <div className="text-[10px] tracking-[0.35em] uppercase text-[#B45309] font-bold">सद्यस्थितीत उपलब्ध अभ्यासक्रम</div>
          <h2 className="font-serif text-3xl md:text-4xl text-[#14532D] mt-2">आमचा व्यवसाय अभ्यासक्रम</h2>
        </div>
        <div className="mt-10 bg-white border border-amber-200/60 shadow-sm overflow-hidden">
          <div className="grid md:grid-cols-[1fr_auto] gap-6 p-8 items-center border-l-4 border-[#F59E0B]">
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#B45309]">एच.एस.सी. व्होकेशनल · MCVC</div>
              <div className="font-serif text-3xl text-[#14532D] mt-1 leading-tight">{trade.nameMr}</div>
              <div className="text-sm text-slate-500 italic mt-0.5">({trade.nameEn})</div>
              <div className="mt-4 grid grid-cols-3 gap-3 text-xs">
                <MiniMeta label="अर्हता" value={trade.qualification} />
                <MiniMeta label="कालावधी" value={trade.duration} />
                <MiniMeta label="प्रवेश क्षमता" value={trade.intake} />
              </div>
            </div>
            <div className="flex items-center justify-center bg-gradient-to-br from-[#14532D] to-[#052E16] text-white w-24 h-24 rounded-full">
              <Car size={40} strokeWidth={1.4} />
            </div>
          </div>
          <div className="border-t border-amber-100 px-8 py-6">
            <Link to="about" className="inline-flex items-center gap-2 text-sm font-bold text-[#14532D] hover:text-[#B45309]">
              सविस्तर अभ्यासक्रम व करिअर संधी पहा <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="bg-gradient-to-b from-[#FEF8E8] to-white">
        <div className="max-w-6xl mx-auto px-5 py-16">
          <div className="text-[10px] tracking-[0.35em] uppercase text-[#B45309] font-bold">संस्थेची वैशिष्ट्ये</div>
          <h2 className="font-serif text-3xl md:text-4xl mt-2 text-[#14532D]">आम्हाला वेगळे बनवणारे</h2>
          <div className="mt-8 grid md:grid-cols-2 gap-4">
            {FEATURES.map((f, i) => (
              <div key={i} className="flex items-start gap-3 bg-white border border-amber-100 p-5">
                <CheckCircle2 className="text-[#F59E0B] mt-0.5 shrink-0" size={20} />
                <div className="text-sm text-slate-800 leading-relaxed">{f}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-5 py-16">
        <div className="bg-gradient-to-br from-[#F59E0B] to-[#B45309] text-[#052E16] p-10 md:p-14 relative overflow-hidden">
          <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-white/20 blur-2xl" />
          <div className="relative grid md:grid-cols-[1fr_auto] items-center gap-8">
            <div>
              <div className="text-[10px] tracking-[0.35em] uppercase font-bold">प्रवेश सुरू</div>
              <h3 className="font-serif text-2xl md:text-4xl mt-2 leading-tight">कौशल्य आधारित करिअरची सुरुवात इथूनच.</h3>
              <p className="mt-3 max-w-xl">प्रवेश, फीस, पात्रता याबाबत आमच्या कार्यालयाशी संपर्क साधा. सोमवार ते शनिवार · सकाळी १० ते सायं. ५ पर्यंत भेट देऊ शकता.</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link to="contact" className="inline-flex items-center gap-2 bg-[#14532D] hover:bg-[#052E16] text-white font-bold text-sm uppercase tracking-wider px-6 py-3.5 transition-all"><Building2 size={16} /> संपर्क</Link>
              <Link to="about" className="inline-flex items-center gap-2 border-2 border-[#14532D] text-[#14532D] font-bold text-sm uppercase tracking-wider px-6 py-3.5 hover:bg-[#14532D] hover:text-white transition-all"><GraduationCap size={16} /> अभ्यासक्रम</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ kpi, label }) {
  return (
    <div>
      <div className="font-serif font-black text-3xl text-[#F59E0B]">{kpi}</div>
      <div className="text-[10px] tracking-[0.2em] uppercase text-white/70 mt-1">{label}</div>
    </div>
  );
}

function MiniMeta({ label, value }) {
  return (
    <div className="border border-amber-200/60 bg-[#FEF8E8] px-3 py-2">
      <div className="text-[9px] tracking-[0.25em] uppercase text-[#B45309] font-bold">{label}</div>
      <div className="text-xs font-bold text-[#14532D] mt-0.5">{value}</div>
    </div>
  );
}
